import { stripIndents } from 'common-tags';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { getLocationNameBooth } from '@components/appointments/utilities/getApptLocation';
import { attendeeStatuses } from './constants';

const getStatusLabel = (status, translate) => {
  switch (status) {
    case attendeeStatuses.ACCEPTED:
      return translate('AppointmentDetailsICS-Accepted');
    case attendeeStatuses.DECLINED:
      return translate('AppointmentDetailsICS-Declined');
    case attendeeStatuses.PENDING:
    case attendeeStatuses.PENDING_PROPOSED:
      return translate('AppointmentDetailsICS-Pending');
    case attendeeStatuses.TENTATIVE:
      return translate('AppointmentDetailsICS-Tentative');
    default:
  }
};

const formatAttendees = (attendees, translate, space) => {
  return attendees
    .map(
      attendee =>
        `${translate('AppointmentDetailsICS-LastFirstName-withStatus', {
          lastName: attendee.lastName,
          firstName: attendee.firstName,
          status: getStatusLabel(attendee.status, translate)
        })}`
    )
    .join(space);
};

const getLocationName = (apptData, translate) => {
  if (!apptData.apptStatus && apptData.location) {
    return apptData.location?.name?.replaceAll(',', '\\,');
  }
  return (
    getLocationNameBooth({
      translate,
      apptStatus: apptData.appointmentStatus,
      parentLocationDisplayName: apptData.parentLocationDisplayName,
      locationDisplayName: apptData.locationDisplayName,
      boothLocationName: apptData.boothLocationName,
      vCal: true
    }) || ''
  );
};

export const formatDescription = ({ apptData, translate, isHTML, isYahoo }) => {
  let description = translate('AppointmentDetailsICS-Label');
  let space = '\\n';
  let { virtualLink } = apptData;
  let requesters;
  let requestees;

  if (apptData.participants?.length > 0) {
    requesters = apptData.participants.filter(h => h.type === 'Host');
    requestees = apptData.participants.filter(h => h.type === 'Attendee');
  }

  // newline character syntax varies across the calendar apps
  if (isYahoo) {
    space = '\n';
  }
  if (isHTML) {
    space = '<br />';
    virtualLink = `<a href=${apptData.virtualLink}>${apptData.virtualLink}</a>`;
  }

  if (apptData.requesters?.length > 0 || requesters) {
    description = description.concat(
      `${space}${space}${translate(
        'AppointmentDetailsICS-HostsHeader'
      )}${space}${formatAttendees(
        apptData.requesters?.length > 0 ? apptData.requesters : requesters,
        translate,
        space
      )}`
    );
  }
  if (apptData.requestees?.length > 0 || requestees) {
    description = description.concat(
      `${space}${space}${translate(
        'AppointmentDetailsICS-AttendeesHeader'
      )}${space}${formatAttendees(
        apptData.requestees?.length > 0 ? apptData.requestees : requestees,
        translate,
        space
      )}`
    );
  }
  if (apptData.description) {
    description = description.concat(
      `${space}${space}${translate(
        'AppointmentDetailsICS-DescriptionHeader'
      )}${space}${apptData.description.replace(/\n/g, space)}`
    );
  }
  if (apptData.virtualLink) {
    description = description.concat(
      `${space}${space}${translate(
        'AppointmentDetailsICS-VirtualLink'
      )}${space}${virtualLink}`
    );
  }

  return description;
};

export const getApptICSString = (apptData, translate) => {
  const icsFormat = "yyyyMMdd'T'HHmmss'Z'";

  const descriptionBody = stripIndents`${formatDescription({
    apptData,
    translate
  })}`;

  const descriptionBodyHTML = stripIndents`${formatDescription({
    apptData,
    translate,
    isHTML: true
  })}`;

  // Trick date-fns into giving us a utc date https://stackoverflow.com/questions/58561169/date-fns-how-do-i-format-to-utc/63227335#63227335
  const formatInUTC = (date, fmt) => {
    return format(utcToZonedTime(date, 'UTC'), fmt, { timeZone: 'UTC' });
  };

  return `
    BEGIN:VEVENT
    DTSTART:${formatInUTC(new Date(apptData.startTime), icsFormat)}
    DTEND:${formatInUTC(new Date(apptData.endTime), icsFormat)}
    SUMMARY:${apptData.name}
    X-ALT-DESC;FMTTYPE=text/html:${descriptionBodyHTML}
    DESCRIPTION:${descriptionBody}
    LOCATION:${getLocationName(apptData, translate)}
  PRIORITY:5
    BEGIN:VALARM
    TRIGGER:-PT15M
    ACTION:DISPLAY
    END:VALARM
    END:VEVENT
  `;
};

export const downloadApptCalendar = (apptData, translate) => {
  if (!apptData) {
    return;
  }

  const a = document.createElement('a');
  a.style.display = 'none';
  document.body.appendChild(a);

  const blobObject = new Blob(
    [
      stripIndents`
        BEGIN:VCALENDAR
        VERSION:2.0
        PRODID:-//Cvent//EN
        ${getApptICSString(apptData, translate)}
        END:VCALENDAR`
    ],
    { type: 'text/calendar;charset=utf-8' }
  );

  a.href = window.URL.createObjectURL(blobObject, 'text/calendar');

  a.setAttribute('download', `${apptData.name}.ics`);
  a.click();

  setTimeout(() => {
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
  }, 100);
};

// Google Calendar automatically converts from UTC to the users configured time zone
export const getApptGoogleCalendar = ({
  name,
  start,
  end,
  description,
  location
}) => {
  const formattedStart = start.replace(/[:.-]/g, '');
  const formattedEnd = end.replace(/[:.-]/g, '');
  const url = new URLSearchParams();

  url.append('text', name);
  url.append('details', description);
  url.append('dates', `${formattedStart}/${formattedEnd}`);
  url.append('location', location !== null ? location : '');

  return `https://calendar.google.com/calendar/u/0/r/eventedit?${url}`;
};

// Outlook Calendar automatically converts from UTC to the users configured time zone
export const getApptOutlookCalendar = ({
  name,
  description,
  start,
  end,
  location,
  inWebView
}) => {
  const url = new URLSearchParams();

  url.append('subject', name);
  url.append('body', description);
  url.append('startdt', start);
  url.append('enddt', end);
  url.append('location', location !== null ? location : '');
  url.append('path', 'calendar/action/compose');
  url.append('rru', 'addevent');

  return inWebView
    ? `https://outlook.office365.com/calendar/0/deeplink/compose?${url}`
    : `https://outlook.office365.com/calendar/action/compose?${url}`;
};

// Yahoo Calendar does NOT automatically convert from UTC to the users time zone so that is done programatically with format()
export const getApptYahooCalendar = ({
  name,
  description,
  start,
  end,
  location
}) => {
  const formattedStart = format(new Date(start), "yyyyMMdd'T'HHmmss");
  const formattedEnd = format(new Date(end), "yyyyMMdd'T'HHmmss");
  const url = new URLSearchParams();

  url.append('title', name);
  url.append('desc', description);
  url.append('st', formattedStart);
  url.append('et', formattedEnd);
  url.append('in_loc', location !== null ? location : '');
  url.append('v', '60'); // Can't find documentation for what this value represents but it's required

  return `https://calendar.yahoo.com/?${url}`;
};
